import { useCallback, useEffect, useState } from "react";
import {
  Button,
  Form,
  Input,
  Modal,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
  message,
} from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";

import { ApiService } from "../../api";
import type { AdminUser, OrgUnit, OrgUnitNode, Position } from "../../api";
import { apiErrorMessage } from "../../api/apiError";
import { SectionLabel } from "../../components/surfaces";
import { space } from "../../theme/space";

const { Text } = Typography;

type Props = {
  units: OrgUnit[];
  onChanged: () => void;
};

/**
 * The organisation chart: units, who heads each, and the positions inside them.
 *
 * A **tree table** rather than a flat list, because the shape is what an
 * administrator is checking: "the head of the requester's unit signs" walks
 * upwards, and a flat list of forty departments says nothing about which of
 * them contains which.
 *
 * The **Head** column carries a warning rather than a blank when nobody is
 * named. An unheaded unit is a perfectly normal, temporary state — and it is
 * also a slot that no case can get past, so it is worth seeing before somebody
 * points an approval at it.
 */
const StructurePanel = ({ units, onChanged }: Props) => {
  const { t } = useTranslation();
  const [tree, setTree] = useState<OrgUnitNode[]>([]);
  const [positions, setPositions] = useState<Position[]>([]);
  const [people, setPeople] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState<OrgUnit | null>(null);
  const [creating, setCreating] = useState(false);
  const [editingPosition, setEditingPosition] = useState<Position | null>(null);
  const [creatingPosition, setCreatingPosition] = useState(false);
  const [form] = Form.useForm();
  const [positionForm] = Form.useForm();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [nodes, rows, everyone] = await Promise.all([
        ApiService.apiAccountsOrgUnitsTree(),
        ApiService.apiAccountsPositionsList(),
        ApiService.apiAccountsAdminUsersList({ is_active: "true" }),
      ]);
      setTree(nodes);
      setPositions(rows);
      setPeople(everyone);
    } catch (e) {
      message.error(apiErrorMessage(e, t("Could not load the organisation")));
    }
    setLoading(false);
  }, [t]);

  useEffect(() => {
    load();
  }, [load]);

  const saveUnit = async () => {
    const values = await form.validateFields();
    try {
      if (creating) await ApiService.apiAccountsOrgUnitsCreate(values);
      else await ApiService.apiAccountsOrgUnitsPartialUpdate(editing!.id, values);
      setEditing(null);
      setCreating(false);
      load();
      onChanged();
    } catch (e) {
      message.error(apiErrorMessage(e, t("Could not save the unit")));
    }
  };

  const savePosition = async () => {
    const values = await positionForm.validateFields();
    try {
      if (creatingPosition) await ApiService.apiAccountsPositionsCreate(values);
      else
        await ApiService.apiAccountsPositionsPartialUpdate(
          editingPosition!.id,
          values,
        );
      setEditingPosition(null);
      setCreatingPosition(false);
      load();
      onChanged();
    } catch (e) {
      message.error(apiErrorMessage(e, t("Could not save the position")));
    }
  };

  const removeUnit = (node: OrgUnitNode) => {
    Modal.confirm({
      title: t("Retire {{name}}?", { name: node.name }),
      content: t(
        "The unit stops being offered anywhere. Nothing is deleted — people who belonged to it keep their history.",
      ),
      okText: t("Retire it"),
      okButtonProps: { danger: true },
      cancelText: t("Cancel"),
      onOk: async () => {
        try {
          await ApiService.apiAccountsOrgUnitsDelete(node.id);
          load();
          onChanged();
        } catch (e) {
          message.error(apiErrorMessage(e, t("Could not retire the unit")));
        }
      },
    });
  };

  const openUnit = (node: OrgUnitNode | null, parentId?: number) => {
    const existing = node ? units.find((u) => u.id === node.id) ?? null : null;
    setCreating(node === null);
    setEditing(existing);
    form.setFieldsValue(
      existing ?? {
        name: "",
        code: "",
        kind: "Department",
        parent: parentId ?? null,
        head: null,
        cost_centre: "",
      },
    );
  };

  const unitColumns = [
    {
      title: t("Unit"),
      dataIndex: "name",
      render: (name: string, row: OrgUnitNode) => (
        <Space size={6}>
          <span style={{ fontWeight: 500 }}>{name}</span>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {row.code}
          </Text>
          {row.source && row.source !== "Manual" && (
            <Tag color="blue" style={{ marginInlineEnd: 0 }}>
              {t("From the directory")}
            </Tag>
          )}
        </Space>
      ),
    },
    {
      title: t("Head"),
      dataIndex: "head_name",
      render: (value: string) =>
        value || (
          <Tooltip
            title={t(
              "No case can get past an approval slot set to 'the head of this unit' until somebody is named.",
            )}
          >
            <Tag color="warning">{t("nobody yet")}</Tag>
          </Tooltip>
        ),
    },
    { title: t("People"), dataIndex: "member_count", width: 100 },
    {
      title: "",
      key: "actions",
      width: 220,
      render: (_: unknown, row: OrgUnitNode) => (
        <Space size={4}>
          <Button size="small" onClick={() => openUnit(row)}>
            {t("Edit")}
          </Button>
          <Button size="small" onClick={() => openUnit(null, row.id)}>
            {t("Add inside")}
          </Button>
          <Button size="small" danger onClick={() => removeUnit(row)}>
            {t("Retire")}
          </Button>
        </Space>
      ),
    },
  ];

  const positionColumns = [
    { title: t("Position"), dataIndex: "title" },
    { title: t("Code"), dataIndex: "code", width: 140 },
    {
      title: t("Unit"),
      dataIndex: "org_unit_name",
      render: (v: string) => v || <Text type="secondary">—</Text>,
    },
    {
      title: t("Reports to"),
      dataIndex: "reports_to_title",
      render: (v: string) => v || <Text type="secondary">—</Text>,
    },
    {
      title: "",
      key: "actions",
      width: 90,
      render: (_: unknown, row: Position) => (
        <Button
          size="small"
          onClick={() => {
            setEditingPosition(row);
            setCreatingPosition(false);
            positionForm.setFieldsValue(row);
          }}
        >
          {t("Edit")}
        </Button>
      ),
    },
  ];

  return (
    <div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: space.snug,
          marginBottom: space.snug,
        }}
      >
        <SectionLabel style={{ flex: 1 }}>{t("Units")}</SectionLabel>
        <Button
          type="primary"
          size="small"
          icon={<PlusOutlined />}
          onClick={() => openUnit(null)}
        >
          {t("New unit")}
        </Button>
      </div>
      <Table
        size="small"
        rowKey="id"
        loading={loading}
        dataSource={tree}
        columns={unitColumns as any}
        pagination={false}
        // Everything open: the shape is the thing being read, and a collapsed
        // tree hides exactly the nesting somebody came here to check.
        defaultExpandAllRows
        locale={{
          emptyText: t(
            "No units yet. Add the departments people belong to, and approval slots can start routing to their managers and heads.",
          ),
        }}
      />

      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: space.snug,
          margin: `${space.band}px 0 ${space.snug}px`,
        }}
      >
        <SectionLabel style={{ flex: 1 }}>{t("Positions")}</SectionLabel>
        <Button
          size="small"
          icon={<PlusOutlined />}
          onClick={() => {
            setCreatingPosition(true);
            setEditingPosition(null);
            positionForm.setFieldsValue({
              title: "",
              code: "",
              org_unit: null,
              reports_to: null,
            });
          }}
        >
          {t("New position")}
        </Button>
      </div>
      <Text type="secondary" style={{ fontSize: 12 }}>
        {t(
          "A position is a chair, not the person in it. Only needed if you set reporting lines by position rather than person to person — the manager on somebody's own record is read first.",
        )}
      </Text>
      <Table
        size="small"
        rowKey="id"
        style={{ marginTop: space.snug }}
        dataSource={positions}
        columns={positionColumns as any}
        pagination={false}
        locale={{ emptyText: t("No positions — reporting lines come from people.") }}
      />

      <Modal
        open={editing !== null || creating}
        title={creating ? t("New unit") : t("Edit unit")}
        onCancel={() => {
          setEditing(null);
          setCreating(false);
        }}
        onOk={saveUnit}
        okText={t("Save")}
        cancelText={t("Cancel")}
      >
        <Form form={form} layout="vertical" style={{ marginTop: space.base }}>
          <Form.Item name="name" label={t("Name")} rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item
            name="code"
            label={t("Code")}
            rules={[{ required: true }]}
            extra={t(
              "A short, stable handle like OPS-KZ. Rules and imports match on this, never on the name — so it should survive a rename.",
            )}
          >
            <Input />
          </Form.Item>
          <div style={{ display: "flex", gap: space.snug }}>
            <Form.Item name="kind" label={t("Kind")} style={{ flex: 1 }}>
              <Select
                options={[
                  { value: "Company", label: t("Company") },
                  { value: "Division", label: t("Division") },
                  { value: "Department", label: t("Department") },
                  { value: "Team", label: t("Team") },
                  { value: "Other", label: t("Other") },
                ]}
              />
            </Form.Item>
            <Form.Item name="parent" label={t("Inside")} style={{ flex: 1 }}>
              <Select
                allowClear
                showSearch
                optionFilterProp="label"
                options={units
                  .filter((u) => u.id !== editing?.id)
                  .map((u) => ({ value: u.id, label: u.name }))}
              />
            </Form.Item>
          </div>
          <Form.Item
            name="head"
            label={t("Headed by")}
            extra={t("Who signs when an approval slot asks for this unit's head.")}
          >
            <Select
              allowClear
              showSearch
              optionFilterProp="label"
              options={people.map((p) => ({
                value: p.id,
                label: `${p.name} · ${p.email}`,
              }))}
            />
          </Form.Item>
          <Form.Item name="cost_centre" label={t("Cost centre")}>
            <Input />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        open={editingPosition !== null || creatingPosition}
        title={creatingPosition ? t("New position") : t("Edit position")}
        onCancel={() => {
          setEditingPosition(null);
          setCreatingPosition(false);
        }}
        onOk={savePosition}
        okText={t("Save")}
        cancelText={t("Cancel")}
      >
        <Form form={positionForm} layout="vertical" style={{ marginTop: space.base }}>
          <Form.Item name="title" label={t("Title")} rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="code" label={t("Code")} rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="org_unit" label={t("Unit")}>
            <Select
              allowClear
              showSearch
              optionFilterProp="label"
              options={units.map((u) => ({ value: u.id, label: u.name }))}
            />
          </Form.Item>
          <Form.Item name="reports_to" label={t("Reports to")}>
            <Select
              allowClear
              showSearch
              optionFilterProp="label"
              options={positions
                .filter((p) => p.id !== editingPosition?.id)
                .map((p) => ({ value: p.id, label: p.title }))}
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default StructurePanel;
